import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, Typography, Button, TextField, MenuItem, Alert, Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  RefreshRounded as RefreshIcon, CompareArrowsRounded as ArbIcon, KeyRounded as KeyIcon,
  InfoOutlined as InfoIcon, StorefrontRounded as BookmakerIcon
} from '@mui/icons-material';
import { apiCall } from '../config';
import { tokens, outcomeLabel } from '../theme';
import BookmakerPicker from './BookmakerPicker';
import BetsTabs from './BetsTabs';
import { PageHeader, CardGridSkeleton, EmptyState, ErrorState, SectionTitle, cardGridSx, formatDay, formatTime } from './ui';

const OUTCOMES = ['home', 'draw', 'away'];

const money = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });

function ArbCard({ event, isArb, lossLabel = 'short of arb' }) {
  const plan = event.stake_plan;
  const name = (k) => (k === 'home' ? event.home_team : k === 'away' ? event.away_team : 'Draw');
  const profitTone = plan.guaranteed_profit > 0 ? tokens.status.good : tokens.status.bad;

  return (
    <Card sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', borderColor: isArb ? alpha(tokens.status.good, 0.4) : tokens.border }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5} mb={1.75}>
        <Box minWidth={0}>
          <Typography variant="caption" color="text.secondary" display="block" noWrap>
            {event.sport} · {formatDay(event.commence_time)} {formatTime(event.commence_time)}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {event.home_team} <Box component="span" color="text.secondary" fontWeight={500}>vs</Box> {event.away_team}
          </Typography>
        </Box>
        <Box textAlign="right" flexShrink={0}>
          <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: '1.35rem', lineHeight: 1.1, color: isArb ? tokens.status.good : 'text.secondary' }}>
            {event.profit_pct > 0 ? '+' : ''}{event.profit_pct}%
          </Typography>
          <Typography variant="caption" color="text.secondary">{isArb ? 'locked in' : lossLabel}</Typography>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap={0.75} mb={1.75}>
        {OUTCOMES.map(k => (
          <Box key={k} display="grid" gridTemplateColumns="auto minmax(0,1fr) auto" alignItems="center" gap={1.25}
            sx={{ p: 1.25, borderRadius: 2, bgcolor: tokens.surfaceRaised, border: `1px solid ${tokens.border}` }}>
            <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 2, bgcolor: tokens.outcome[k] }} />
            <Box minWidth={0}>
              <Typography variant="body2" fontWeight={600} noWrap title={name(k)}>{name(k)}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {outcomeLabel[k]} at <Box component="span" color="text.primary" fontWeight={600}>{event.best[k].bookmaker}</Box>
              </Typography>
            </Box>
            <Box textAlign="right">
              <Typography variant="body2" fontWeight={700}>Stake {money(plan.stakes[k])}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                @ {(event.best[k].quoted ?? event.best[k].price).toFixed(2)}
              </Typography>
              {event.best[k].commission > 0 && (
                <Tooltip title={`Exchange: ${Math.round(event.best[k].commission * 100)}% commission on winnings is already included in the stake and profit`}>
                  <Typography variant="caption" color="warning.main" sx={{ fontSize: 10.5, cursor: 'help' }}>
                    net {event.best[k].price.toFixed(2)} after {Math.round(event.best[k].commission * 100)}%
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <Box mt="auto" display="flex" justifyContent="space-between" alignItems="flex-end" gap={1}>
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">
            Stake {money(plan.total_staked)} · returns ≥ {money(plan.guaranteed_return)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {event.bookmaker_count} bookmakers · book {(event.book_sum * 100).toFixed(1)}%
          </Typography>
        </Box>
        <Typography fontWeight={700} sx={{ color: profitTone, whiteSpace: 'nowrap' }}>
          {plan.guaranteed_profit > 0 ? '+' : ''}{money(plan.guaranteed_profit)}
        </Typography>
      </Box>
    </Card>
  );
}

const Arbitrage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [stake, setStake] = useState(100);
  const [roundTo, setRoundTo] = useState(1);

  const fetchArbitrage = useCallback(async (refresh = false) => {
    try {
      refresh ? setRefreshing(true) : setLoading(prev => prev && !data);
      const params = new URLSearchParams({ stake: stake || 100, round: roundTo, ...(refresh && { refresh: '1' }) });
      const response = await apiCall(`/api/arbitrage?${params}`);
      if (!response.ok) throw new Error(`Request failed (${response.status})`);
      setData(await response.json());
      setError(null);
    } catch (err) {
      setError('Could not load arbitrage opportunities.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stake, roundTo]);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [savingBookmakers, setSavingBookmakers] = useState(false);

  const saveBookmakers = async (bookmakers) => {
    setSavingBookmakers(true);
    try {
      const response = await apiCall('/api/settings', { method: 'PUT', body: JSON.stringify({ bookmakers }) });
      if (!response.ok) throw new Error();
      setPickerOpen(false);
      await fetchArbitrage(false);
    } catch {
      setError('Could not save your bookmakers.');
    } finally {
      setSavingBookmakers(false);
    }
  };

  const selected = useMemo(() => data?.selected_bookmakers || [], [data]);
  const bookmakerLabel = selected.length
    ? `${selected.length} bookmaker${selected.length === 1 ? '' : 's'}`
    : 'All bookmakers';

  // Stake changes only re-read the cached scan, so a short debounce is enough
  useEffect(() => {
    const timer = setTimeout(() => fetchArbitrage(false), 300);
    return () => clearTimeout(timer);
  }, [fetchArbitrage]);

  const controls = data?.configured && (
    <Card sx={{ p: 1.5, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
      <TextField
        label="Total stake"
        type="number"
        size="small"
        value={stake}
        onChange={(e) => setStake(parseFloat(e.target.value) || '')}
        inputProps={{ min: 1, inputMode: 'decimal' }}
        sx={{ width: { xs: 'calc(50% - 5px)', sm: 160 } }}
      />
      <TextField select label="Round stakes to" size="small" value={roundTo} onChange={(e) => setRoundTo(e.target.value)} sx={{ width: { xs: 'calc(50% - 5px)', sm: 150 } }}>
        {[0.01, 1, 5, 10].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
      </TextField>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<BookmakerIcon />}
        onClick={() => setPickerOpen(true)}
        disabled={!data.bookmakers?.length}
        sx={{ width: { xs: '100%', sm: 'auto' }, borderColor: 'divider', height: 40 }}
      >
        {bookmakerLabel}
      </Button>
      <Box flex={1} minWidth={{ xs: '100%', sm: 0 }} display="flex" alignItems="center" justifyContent={{ xs: 'space-between', sm: 'flex-end' }} gap={1.5}>
        <Typography variant="caption" color="text.secondary" textAlign={{ sm: 'right' }}>
          {data.scanned_at ? `Scanned ${new Date(data.scanned_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Not scanned yet'}
          {data.requests_remaining !== null && data.requests_remaining !== undefined && <><br />{data.requests_remaining} API credits left</>}
        </Typography>
        <Tooltip title="One fresh scan every 30 minutes at most, to stay inside the free API tier">
          <span>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchArbitrage(true)} disabled={refreshing}>
              {refreshing ? 'Scanning…' : 'Rescan'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Card>
  );

  return (
    <Box>
      <PageHeader
        title="Arbitrage"
        subtitle="Bookmakers sometimes disagree enough that backing every outcome, each at the bookmaker paying most, returns more than you stake whatever happens."
      />
      <BetsTabs />

      {loading ? (
        <CardGridSkeleton count={3} height={260} />
      ) : error ? (
        <ErrorState message={error} onRetry={() => fetchArbitrage(false)} />
      ) : !data.configured ? (
        <EmptyState
          icon={<KeyIcon />}
          title="Connect a multi-bookmaker odds feed"
          body={<>Arbitrage needs prices from many bookmakers. Get a free key from the-odds-api.com, then run <Box component="code" sx={{ px: 0.75, py: 0.25, borderRadius: 1, bgcolor: tokens.surfaceRaised, fontSize: 12 }}>npx wrangler secret put ODDS_API_KEY</Box> in the cloudflare-worker folder.</>}
        />
      ) : (
        <>
          {controls}

          {data.mode === 'single' ? (
            <>
              <Card sx={{ p: 2, mb: 3, bgcolor: alpha(tokens.accent, 0.08), borderColor: alpha(tokens.accent, 0.3) }}>
                <Typography variant="subtitle2" gutterBottom>
                  One bookmaker: {data.bookmakers?.find(b => b.key === selected[0])?.title || selected[0]}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  A single bookmaker's margin means covering every outcome always loses a little, so there's no arb here.
                  These are its matches with the smallest loss, i.e. its thinnest margins and the best value for any bet.
                </Typography>
                <Button size="small" onClick={() => setPickerOpen(true)} sx={{ mt: 1, ml: -1 }}>Add more bookmakers to find arbs</Button>
              </Card>
              {data.lowest_loss?.length ? (
                <>
                  <SectionTitle>Smallest loss first</SectionTitle>
                  <Box sx={cardGridSx}>
                    {data.lowest_loss.map(e => <ArbCard key={e.id} event={e} lossLabel="loss" />)}
                  </Box>
                </>
              ) : (
                <EmptyState icon={<ArbIcon />} title="No upcoming matches priced by this bookmaker" body="Try another bookmaker or rescan later." />
              )}
            </>
          ) : data.opportunities.length === 0 ? (
            <EmptyState
              icon={<ArbIcon />}
              title="No guaranteed-profit bets right now"
              body={selected.length
                ? `Nothing across your ${selected.length} bookmakers at the moment. Adding more bookmakers finds more arbs.`
                : `True arbs are rare and usually close within minutes.${data.near_misses.length ? ' The closest candidates are below.' : ''}`}
              action={selected.length ? <Button variant="outlined" onClick={() => setPickerOpen(true)}>Change bookmakers</Button> : null}
            />
          ) : (
            <Box sx={cardGridSx}>
              {data.opportunities.map(e => <ArbCard key={e.id} event={e} isArb />)}
            </Box>
          )}

          {data.mode !== 'single' && data.near_misses.length > 0 && (
            <>
              <SectionTitle>Closest to an arb</SectionTitle>
              <Typography variant="body2" color="text.secondary" mt={-0.5} mb={2}>
                These would still lose a little if placed now. Worth watching for price moves.
              </Typography>
              <Box sx={cardGridSx}>
                {data.near_misses.map(e => <ArbCard key={e.id} event={e} />)}
              </Box>
            </>
          )}

          <BookmakerPicker
            open={pickerOpen}
            bookmakers={data.bookmakers || []}
            selected={selected}
            saving={savingBookmakers}
            onClose={() => setPickerOpen(false)}
            onSave={saveBookmakers}
          />

          <Alert severity="warning" icon={<InfoIcon />} sx={{ mt: 4, bgcolor: alpha(tokens.status.warn, 0.08), border: `1px solid ${alpha(tokens.status.warn, 0.25)}` }}>
            Prices move. Confirm each price at the bookmaker before betting, place the largest stake first, and expect bookmakers to limit accounts that regularly bet arbs.
          </Alert>
        </>
      )}
    </Box>
  );
};

export default Arbitrage;
